import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllSources, deleteSource } from '../../services/sourceService';
import SourceForm from './SourceForm';
import ResetStatusButton from './ResetStatusButton';
import { SourceItem } from '../../../../models';
import { formatCronSchedule } from '../../utils/formatUtils';
import {
  Button,
  Badge,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Dialog,
  LoadingOverlay,
} from '../ui';
import { Pencil, Plus, Trash2 } from '../ui/icons';

// Helper to get human readable cron schedule
function getHumanReadableSchedule(value: string): string {
  if (!value) return 'No automatic scheduling - manual processing only';
  return formatCronSchedule(value);
}

const SourcesList: React.FC = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedSource, setSelectedSource] = useState<SourceItem | null>(null);

  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await getAllSources();
      const sorted = [...response].sort((a, b) => {
        const domainA = a.domain || '';
        const domainB = b.domain || '';
        if (!domainA && !domainB) return a.name.localeCompare(b.name);
        if (!domainA) return 1;
        if (!domainB) return -1;
        return domainA.localeCompare(domainB, undefined, { sensitivity: 'base' });
      });
      setSources(sorted);
    } catch (error) {
      console.error('Error loading sources:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSources();
  }, []);

  const handleEdit = (source: SourceItem) => {
    navigate(`/sources/${source.id}`);
  };

  const handleDelete = (source: SourceItem) => {
    setSelectedSource(source);
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    if (selectedSource) {
      try {
        await deleteSource(selectedSource.id);
        await loadSources();
        setOpenDelete(false);
      } catch (error) {
        console.error('Error deleting source:', error);
      }
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'PROCESSING':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'IDLE':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSourceTypeBadgeVariant = (sourceType?: string) => {
    switch (sourceType) {
      case 'SEARCH_RESULTS':
        return 'secondary';
      case 'SITEMAP':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Sources</h1>
        <Button onClick={() => setOpenForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Source
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <LoadingOverlay />
        ) : sources.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No sources found
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Domain</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Processing</TableHeader>
                <TableHeader>Schedule</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.map((source) => (
                <TableRow
                  key={source.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleEdit(source)}
                >
                  <TableCell className="font-medium">{source.name}</TableCell>
                  <TableCell>{source.domain || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={getSourceTypeBadgeVariant(source.sourceType)} size="sm">
                      {source.sourceType || 'URL_LIST'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {source.processingStatus && (
                      <Badge variant={getStatusBadgeVariant(source.processingStatus)} size="sm">
                        {source.processingStatus}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {getHumanReadableSchedule(source.scrapingSchedule || '')}
                  </TableCell>
                  <TableCell>{new Date(source.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(source)}
                        className="p-1 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <ResetStatusButton
                        sourceId={source.id}
                        sourceName={source.name}
                        currentStatus={source.processingStatus || 'IDLE'}
                        onResetComplete={loadSources}
                        disabled={false}
                      />
                      <button
                        onClick={() => handleDelete(source)}
                        className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <SourceForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSave={loadSources}
      />

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        title="Delete Source"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpenDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          Are you sure you want to delete the source "{selectedSource?.name}"? This action cannot be undone.
        </p>
      </Dialog>
    </div>
  );
};

export default SourcesList;
